import { Button, Icon, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { FaMicrophone, FaStop } from 'react-icons/fa';

export type AudioRecorderRef = {
  onSave: () => Promise<File | null>;
  onClear: () => void;
};

const AudioRecorder = React.forwardRef<AudioRecorderRef>((_props, ref) => {
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const chunksRef = React.useRef<Blob[]>([]);

  const [isRecording, setIsRecording] = React.useState(false);
  const [audioUrl, setAudioUrl] = React.useState<string | null>(null);
  const [blob, setBlob] = React.useState<Blob | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const stopStream = React.useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  React.useEffect(() => stopStream, [stopStream]);

  const startRecording = React.useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      streamRef.current = stream;
      chunksRef.current = [];

      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const recordedBlob = new Blob(chunksRef.current, {
          type: 'audio/webm',
        });
        setBlob(recordedBlob);
        setAudioUrl(URL.createObjectURL(recordedBlob));
        stopStream();
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      setError('Não foi possível acessar o microfone');
    }
  }, [stopStream]);

  const stopRecording = React.useCallback(() => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  }, []);

  const handleReset = React.useCallback(() => {
    setAudioUrl(null);
    setBlob(null);
  }, []);

  React.useImperativeHandle(ref, () => ({
    onSave: async () => {
      if (!blob) return null;
      return new File([blob], `audio-${Date.now()}.webm`, {
        type: blob.type,
      });
    },
    onClear: handleReset,
  }));

  return (
    <VStack spacing="4" align="stretch">
      {error && (
        <Text color="red.500" fontSize="sm">
          {error}
        </Text>
      )}
      {audioUrl ? (
        <VStack align="stretch" spacing="2">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio controls src={audioUrl} style={{ width: '100%' }} />
          <Button size="sm" variant="ghost" onClick={handleReset}>
            Gravar novamente
          </Button>
        </VStack>
      ) : (
        <Button
          leftIcon={<Icon as={isRecording ? FaStop : FaMicrophone} />}
          colorScheme={isRecording ? 'red' : 'brand'}
          onClick={isRecording ? stopRecording : startRecording}
        >
          {isRecording ? 'Parar gravação' : 'Iniciar gravação'}
        </Button>
      )}
    </VStack>
  );
});

AudioRecorder.displayName = 'AudioRecorder';

export default AudioRecorder;
